import React, {
  Dispatch, SetStateAction, useContext, useEffect,
} from 'react';
import FirepadComponent from '../../../shared/components/firepad/component';
import { getFirepadPathV3, getFirepadPathV2, getFirepadSecretChatPath } from '../../../utils/firebase';
import ProseMirrorComponentV2 from '../../../shared/components/prosemirror/index';
import ProseMirrorComponentV1 from '../../../shared/components/prosemirror/index_old';
import MeetingDataContext from '../context/MeetingDataContext';
import PreviousMeetingDataContext from '../../../shared/components/previous-meeting-notes/context/PreviousMeetingDataContext';
import NotesLoading from './NotesLoading';
import { NoteType, Shortcut } from '../../../shared/types/types';
import { startTrackingEvent, stopTrackingEvent } from '../../../utils/analytics/eventLogger';
import { AGENDA, PRIVATE_NOTES, USER_CENTER_CURRENT_MEETING } from '../../../utils/enums';
import { NotesFor } from '../../../utils/analytics/enums';

interface NotesProps {
  page: NoteType,
  userId: string,
  meetingId: string,
  setNotesContent: Dispatch<SetStateAction<string>>,
  setIsSynchronized: Dispatch<SetStateAction<boolean>>
  setProseMirrorEditorView: Dispatch<SetStateAction<any>>
  isPreviousMeeting?: boolean
  // eslint-disable-next-line no-unused-vars
  onShortcutTrigger: (shortcut: Shortcut) => void,
  displayNotes?: boolean,
  secretChatId?: string,
  notesFor?: NotesFor,
}

const Notes = ({
  page,
  userId,
  meetingId,
  setNotesContent,
  setIsSynchronized,
  setProseMirrorEditorView,
  isPreviousMeeting = false,
  onShortcutTrigger,
  displayNotes = true,
  secretChatId,
  notesFor = USER_CENTER_CURRENT_MEETING,
}: NotesProps) => {
  // We had an issue with the notes, that when running the CE to point to localhost,
  // and creating a new meeting in calendar then we would get an Permissions Denied error.
  // This was most likely because of realtime database security rules
  // Specificially the ".validate": "newData.hasChild('history')"
  // With the new database architecture, hopefully this issue will be resolved

  const meetingData = isPreviousMeeting
    ? useContext(PreviousMeetingDataContext)
    : useContext(MeetingDataContext);

  useEffect(() => {
    if (meetingData.resolvedState === 'pending') {
      startTrackingEvent('notesLoad');
    } else {
      stopTrackingEvent('notesLoad');
    }
  }, [meetingData.resolvedState]);

  if (meetingData.resolvedState === 'pending') return <NotesLoading />;

  if (secretChatId?.length !== 0 && page === PRIVATE_NOTES) {
    if (meetingData.version >= 5) {
      return (
        <ProseMirrorComponentV2
          path={getFirepadSecretChatPath(secretChatId!, 'prosemirror')}
          page={page}
          setHtml={setNotesContent}
          setIsSynchronized={setIsSynchronized}
          setProseMirrorEditorView={setProseMirrorEditorView}
          displayNotes={displayNotes}
          isPreviousMeeting={isPreviousMeeting}
          notesFor={notesFor}
        />
      );
    }

    if (meetingData.version >= 3) {
      return (
        <ProseMirrorComponentV1
          path={getFirepadSecretChatPath(secretChatId!, 'prosemirror')}
          page={page}
          setHtml={setNotesContent}
          setIsSynchronized={setIsSynchronized}
          setProseMirrorEditorView={setProseMirrorEditorView}
          displayNotes={displayNotes}
          isPreviousMeeting={isPreviousMeeting}
          notesFor={notesFor}
        />
      );
    }
    return (
      <>
        <FirepadComponent
          path={getFirepadSecretChatPath(secretChatId!, 'firepad')}
          setIsSynchronized={setIsSynchronized}
          setHtml={setNotesContent}
          page={page}
          isPreviousMeeting={isPreviousMeeting}
          onShortcutTrigger={onShortcutTrigger}
          notesFor={notesFor}
        />
      </>
    );
  }
  // For meetingVersion 4 or above, we don't want to show the agenda notes
  // because we only want the user to use the shared notes
  if (meetingData.version >= 4 && page === AGENDA) return null;

  // TODO: This is a temporary forced change.
  // return (
  //   <ProseMirrorComponent
  //     path={getFirepadPathV3(page, meetingId, 'prosemirror', userId)}
  //     page={page}
  //     setHtml={setNotesContent}
  //     setIsSynchronized={setIsSynchronized}
  //     setProseMirrorEditorView={setProseMirrorEditorView}
  //     displayNotes={displayNotes}
  //     isPreviousMeeting={isPreviousMeeting}
  //   />
  // );
  if (meetingData.version >= 5) {
    const prosemirrorPath = getFirepadPathV3(page, meetingId, 'prosemirror', userId);
    return (
      <ProseMirrorComponentV2
        path={prosemirrorPath}
        page={page}
        setHtml={setNotesContent}
        setIsSynchronized={setIsSynchronized}
        setProseMirrorEditorView={setProseMirrorEditorView}
        displayNotes={displayNotes}
        isPreviousMeeting={isPreviousMeeting}
        notesFor={notesFor}
      />
    );
  }

  if (meetingData.version >= 3 && page !== PRIVATE_NOTES) {
    return (
      <ProseMirrorComponentV1
        path={getFirepadPathV2(page, meetingId, 'prosemirror', userId)}
        page={page}
        setHtml={setNotesContent}
        setIsSynchronized={setIsSynchronized}
        setProseMirrorEditorView={setProseMirrorEditorView}
        displayNotes={displayNotes}
        isPreviousMeeting={isPreviousMeeting}
        notesFor={notesFor}
      />
    );

    // return (
    //   <ProseMirrorComponent
    //     path={getFirepadPathV2(page, meetingId, 'prosemirror', userId)}
    //     page={page}
    //     setHtml={setNotesContent}
    //     setIsSynchronized={setIsSynchronized}
    //     setProseMirrorEditorView={setProseMirrorEditorView}
    //     displayNotes={displayNotes}
    //     isPreviousMeeting={isPreviousMeeting}
    //   />
    // );
  }
  return (
    <>
      <FirepadComponent
        path={getFirepadPathV2(page, meetingId, 'firepad', userId)}
        setIsSynchronized={setIsSynchronized}
        setHtml={setNotesContent}
        page={page}
        isPreviousMeeting={isPreviousMeeting}
        onShortcutTrigger={onShortcutTrigger}
        notesFor={notesFor}
      />
    </>
  );
};

Notes.defaultProps = {
  isPreviousMeeting: false,
  displayNotes: true,
  secretChatId: '',
  notesFor: USER_CENTER_CURRENT_MEETING,
};

export default Notes;
